import type { IntakePrefill } from '@lixionary-jira/domain';
import type { SuppliedAttachment } from '../tickets/TicketCreator';
import type { TicketType } from '@lixionary-jira/domain';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock } from 'lucide-react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';
import { intakeApi } from '../../api/client';
import { intakeCreation } from '../../api/creation';
import { Button } from '../../ui/Button';
import { ErrorAlert, PageLoading } from '../../ui/feedback';
import { PageHeader, usePageTitle } from '../../ui/misc';
import { TicketCreator } from '../tickets/TicketCreator';
import { notifyOpener, type IntakeCreated } from './opener';

/** The intake popup's session (JIRA-19): another platform's service account reporting for one person. */
export interface IntakeMe {
  project: { key: string; name: string; archived: boolean };
  reporter: { displayName: string; email: string | null };
  source: string;
  service: { name: string };
  type: TicketType | null;
  returnOrigin: string;
  csrfToken: string;
  expiresAt: string;
  assistant: { available: boolean; model: string; reviewOnCreate: boolean };
  canAttach: boolean;
  prefill?: IntakePrefill;
  attachments?: SuppliedAttachment[];
  uploadLimits?: { file: number; video: number; count: number };
}

const IntakeContext = createContext<IntakeMe | null>(null);

function useIntake(): IntakeMe {
  const me = useContext(IntakeContext);
  if (!me) throw new Error('useIntake outside IntakeLayout');
  return me;
}

/** Ends the intake session, then closes the popup; the platform that opened it keeps its own page. */
async function closeIntake() {
  await intakeApi.post('/session/end').catch(() => undefined);
  window.close();
}

/** Shell for the popup: no navigation into the rest of Jira, only who is reporting where. */
export function IntakeLayout() {
  const q = useQuery({
    queryKey: ['intake-me'],
    queryFn: () => intakeApi.get<IntakeMe>('/me'),
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      query.state.data?.attachments?.some((f) => f.scanState === 'pending') ? 2000 : false,
  });
  if (q.isPending) return <PageLoading />;
  if (q.error) return <IntakeExpiredPage />;
  intakeApi.setCsrfToken(q.data.csrfToken);
  return (
    <IntakeContext.Provider value={q.data}>
      <header className="topbar intake-bar">
        <div className="grow intake-who">
          <strong>{q.data.project.name}</strong>
          <span className="small muted">
            Reporting as {q.data.reporter.displayName} via {q.data.source}
          </span>
        </div>
      </header>
      <main className="page narrow">
        <Outlet />
      </main>
    </IntakeContext.Provider>
  );
}

export function IntakeNewPage() {
  const me = useIntake();
  const navigate = useNavigate();
  const feature = me.type === 'feature_request';
  usePageTitle(`${feature ? 'Request a feature' : 'Report an issue'} · ${me.project.name}`);
  return (
    <>
      <PageHeader
        title={feature ? 'Request a feature' : 'Report an issue'}
        subtitle={`Your request goes to the ${me.project.name} team. They review it, may ask questions, and decide what happens next.`}
      />
      <TicketCreator
        creation={intakeCreation(me.reporter.displayName)}
        assistant={me.assistant}
        initialType={me.type ?? 'issue'}
        prefill={me.prefill ?? {}}
        suppliedAttachments={me.attachments ?? []}
        suppliedBy={me.source}
        {...(me.uploadLimits ? { uploadLimits: me.uploadLimits } : {})}
        allowAttachments={me.canAttach}
        cancel={
          <Button variant="ghost" onClick={() => void closeIntake()}>
            Cancel
          </Button>
        }
        onCreated={(t) => navigate(`/intake/done/${t.number}`, { replace: true })}
      />
    </>
  );
}

export function IntakeDonePage() {
  const me = useIntake();
  const { number } = useParams();
  const ticket = useQuery({
    queryKey: ['intake-ticket', number],
    queryFn: () => intakeApi.get<IntakeCreated>(`/tickets/${number}`),
    retry: false,
  });
  const sent = useRef(false);
  const [delivered, setDelivered] = useState<boolean | null>(null);
  useEffect(() => {
    if (!ticket.data || sent.current) return;
    sent.current = true;
    setDelivered(notifyOpener(window, me.returnOrigin, ticket.data));
  }, [ticket.data, me.returnOrigin]);
  usePageTitle(ticket.data ? `Submitted ${ticket.data.key}` : null);
  if (ticket.isPending) return <PageLoading />;
  if (ticket.error) return <ErrorAlert error={ticket.error} />;
  return (
    <div className="auth-card wide stack">
      <CheckCircle2 size={28} aria-hidden />
      <h1 className="page-title">Submitted {ticket.data.key}</h1>
      <p className="muted">
        <strong className="ink">{ticket.data.title}</strong> is now with the {me.project.name} team. You can
        follow its status in {me.source}.
      </p>
      {delivered === false && <p className="small muted">You can close this window.</p>}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <Button onClick={() => void closeIntake()}>Close</Button>
      </div>
    </div>
  );
}

export function IntakeExpiredPage() {
  usePageTitle('Report window expired');
  return (
    <div className="auth-layout">
      <div className="auth-card stack">
        <Clock size={28} aria-hidden />
        <h1 className="page-title">This report window has expired</h1>
        <p className="muted">
          Close it and start the report again from the application you came from. Nothing was submitted from
          this window after it expired.
        </p>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => window.close()}>
            Close window
          </Button>
        </div>
      </div>
    </div>
  );
}
